/**
 * expr 的**白名单**：源码里能碰到的每一个名字都在这里。
 *
 * 这是 expr 之所以是**沙箱**而不只是「不用 eval」的原因：
 * 源码根本不经过 JS 引擎，能碰到什么完全由下面两张表决定 ——
 * 摸不到 `globalThis`、`fetch`、原型链。
 *
 * ⚠️ 加一项之前先问：它**确定**吗？`now()` / `random()` 会让同一份文档
 * 两次求值给出不同结果，那等于文档不可复现 —— 这类东西不进白名单。
 */
import type { ExprNode } from './ast'

/** `api` 上**可以调用**的方法 */
export const API_METHODS = [
  'input',
  'repeat',
  'literal',
  'socketType',
  'runSubgraph',
  /*
   * **写多个输出**：`api.outputs('out', [a, b, c])` → `out.0` / `out.1` / `out.2`。
   *
   * 为什么需要它：`({ out: … })` 的键是**写死的**，而输出变参行的实例数
   * 是运行时才知道的（由节点自己算）—— 静态键表达不了。
   *
   * ⚠️ 只对**输出方向**的变参行有效；写输入变参行会抛错
   * （输入是别人喂进来的，求值器改不了）。
   */
  'outputs',
] as const

/**
 * `api` 上**可以读**（不调用）的属性。
 *
 * `model` 是当前节点的定义（行、宽度…），`data` 是宿主注入的数据源表。
 * 它们都是**用户自己的数据**，读没有风险；而 `input()` 这类方法要能调用，
 * 所以两个表分开。
 */
export const API_PROPS = ['model', 'data'] as const

export type ApiMethod = (typeof API_METHODS)[number]
export type ApiProp = (typeof API_PROPS)[number]

/**
 * 静态检查：这个成员的读法合法吗？
 *
 * 只有 `api` / `helpers` 这两个根受**属性名**约束；
 * 更深一层（`api.model.rows`、`api.input('a').length`）是**数据访问**，
 * 编译期不限制属性名，改由运行时挡危险键（见 `evaluate.ts` 的 `blockedKey`）。
 */
export function isApiMethod(name: string): name is ApiMethod {
  return (API_METHODS as readonly string[]).includes(name)
}

export function isApiProp(name: string): name is ApiProp {
  return (API_PROPS as readonly string[]).includes(name)
}

/**
 * 被调者必须是「根上的直接成员」：`api.x(...)` 或 `helpers.x(...)`。
 *
 * ⚠️ 这条规则是**挡逃逸的关键**：没有它，`x.call(null)` /
 * `x.constructor(...)` 这类「把数据里的函数拿出来调」就走通了。
 * 加上它之后，源码能调用的函数集合在**编译期**就穷尽了。
 */
export function rootMemberOf(node: ExprNode): { root: string; member: string } | null {
  if (node.kind !== 'member' || node.object.kind !== 'ref') return null
  return { root: node.object.name, member: node.property }
}
